// Board TILE STATE visuals — per-frame render, mode skins (armed/eaten/scanned/liar/scramble),
// label occlusion, munch/hazard/scan VFX. Sub-split from boardView.ts (FACE&FURY S6, behavior-frozen):
// bodies byte-identical to the monolith; only the containing file changed. Bound onto GameScene via
// the boardView.ts barrel — do NOT import sibling controllers (S2 rule); cross-calls go through `this`.
import Phaser from 'phaser';
import type { Snapshot, Cell } from '../../core';
import { PALETTE, hex, tex, hasTex } from '../keys';
import { particleBurst, floatText, glowPulse } from '../juice';
import { audio } from '../audio';
import { haptic, motionAllowed } from '../settings';
import { drawIcon } from '../icons';
import { PLAYER, AI, type CellMode, type CellView } from './types';
import type { GameScene } from '../scenes/GameScene';

function armedAnswerY(v: CellView): number {
  return -v.h * 0.11;
}

function paintArmedAnswerPlate(v: CellView): void {
  const plateW = Math.min(v.w - 10, Math.max(34, v.label.width + 12));
  const plateH = Math.min(Math.max(20, v.label.height + 6), v.h * 0.26);
  v.labelPlate
    .clear()
    .fillStyle(PALETTE.void, 0.72)
    .fillRoundedRect(-plateW / 2, armedAnswerY(v) - plateH / 2, plateW, plateH, 5)
    .lineStyle(1, PALETTE.white, 0.12)
    .strokeRoundedRect(-plateW / 2, armedAnswerY(v) - plateH / 2, plateW, plateH, 5)
    .setVisible(v.label.text.length > 0);
}

function styleArmedAnswerLabel(v: CellView): void {
  v.label
    .setColor(hex(PALETTE.white))
    .setAlpha(0.86)
    .setY(armedAnswerY(v))
    .setFontSize(Math.max(11, Math.min(15, Math.floor(v.w * 0.2))))
    .setWordWrapWidth(v.w - 8, false)
    .setStroke('#020617', 3);
  paintArmedAnswerPlate(v);
}

function popCorrectClaimTile(scene: GameScene, v: CellView, cellId: number): void {
  if (!motionAllowed() || !v.container.active) return;
  const token = `${cellId}:${Math.round(scene.time.now)}`;
  v.container.setData('__correctClaimPop', token);
  const flash = scene.add.graphics();
  flash
    .fillStyle(PALETTE.white, 0.34)
    .fillRoundedRect(-v.w / 2 + 4, -v.h / 2 + 4, v.w - 8, v.h - 8, 9)
    .lineStyle(2, PALETTE.white, 0.42)
    .strokeRoundedRect(-v.w / 2 + 4, -v.h / 2 + 4, v.w - 8, v.h - 8, 9);
  v.container.add(flash);
  scene.tweens.killTweensOf(v.container);
  v.container.setScale(1);
  scene.tweens.add({
    targets: v.container,
    scaleX: 1.3,
    scaleY: 1.3,
    duration: 75,
    ease: 'Back.easeOut',
    yoyo: true,
    onComplete: () => {
      if (!v.container.active || v.container.getData('__correctClaimPop') !== token) return;
      v.container.setScale(1);
    },
  });
  scene.tweens.add({
    targets: flash,
    alpha: 0,
    duration: 105,
    ease: 'Quad.easeOut',
    onComplete: () => flash.destroy(),
  });
}


  /**
   * Per-frame: keep the player's current answer readable while still fading AI/hazard-occupied labels.
   * The human's label is lifted inside its tile so captures cannot confuse an adjacent label for the
   * current cell; AI/hazard cells are not the player's active decision and can stay visually quiet.
   */
export function applyLabelOcclusion(this: GameScene, snap: Snapshot): void {
    const affected = new Set<number>();
    const occupied = new Set<number>();
    const hazardOccupied = new Set<number>();
    const human = snap.players.find((p) => p.id === PLAYER);
    const humanPos = human && human.alive ? human.pos : -1;
    if (humanPos >= 0) affected.add(humanPos);
    if (this.aiEnabled) {
      const ai = snap.players.find((p) => p.id === AI);
      if (ai && ai.alive) occupied.add(ai.pos);
    }
    // S3 item 9: fade the word under the ghost racer too, so its 'YOUR GHOST' tag never overlaps a tile
    // label. Treated like the AI/hazard occupants (dimmed, not top-pinned).
    const ghostCell = this.ghostRacer?.cell;
    if (ghostCell !== undefined && ghostCell >= 0) occupied.add(ghostCell);
    for (const hz of snap.hazards) {
      occupied.add(hz.pos);
      hazardOccupied.add(hz.pos);
    }
    for (const id of occupied) affected.add(id);
    for (const id of this.occludedLabels) {
      if (!affected.has(id)) {
        const v = this.cellViews[id];
        if (v) {
          if (v.mode === 'armed') {
            styleArmedAnswerLabel(v);
          } else {
            v.label.setAlpha(1).setY(0).setStroke('#020617', 2);
          }
          v.stateFrame.setAlpha(1);
          v.glyph.setAlpha(1);
        }
      }
    }
    if (humanPos >= 0) {
      const v = this.cellViews[humanPos];
      // Top-pin a real, high-contrast label while the muncher stands here. The under-board strip still
      // carries the full current-tile read, but the tile itself should not look blank in screenshots.
      if (v) {
        const eaten = v.mode === 'eatenCorrect' || v.mode === 'eatenDecoy';
        if (v.mode === 'armed') {
          styleArmedAnswerLabel(v);
        } else {
          v.label.setAlpha(0.88).setY(-v.h * (eaten ? 0.18 : 0.36)).setStroke('#020617', 3);
        }
      }
    }
    for (const id of occupied) {
      if (id === humanPos) continue;
      const v = this.cellViews[id];
      if (v) {
        if (v.mode === 'armed') {
          styleArmedAnswerLabel(v);
          v.label.setAlpha(0.78);
        } else {
          v.label.setAlpha(0.7).setY(-v.h * 0.34).setStroke('#020617', 3);
        }
      }
    }
    for (const id of hazardOccupied) {
      const v = this.cellViews[id];
      if (!v) continue;
      // The hazard sprite is the primary state. Keep scanned/wrong markers visible, but lower their
      // emphasis so a tile cannot read as four competing states at once under the hazard body.
      if (v.mode === 'revealDecoy' || v.mode === 'eatenDecoy') {
        v.stateFrame.setAlpha(0.58);
        v.glyph.setAlpha(0.78);
      }
    }
    this.occludedLabels = affected;
  }



export function renderCell(this: GameScene, cell: Cell, v: CellView, immediate: boolean): void {
    // Wall: dark, no text. (Walls never arm / get eaten / scan-reveal.)
    if (cell.kind === 'wall') {
      if (v.mode !== 'neutral' || v.label.text !== '') {
        // Walls never carry tile art — hide any underlay and paint the dark wall fill opaque.
        v.tileImg?.setVisible(false);
        v.bg.setFillStyle(PALETTE.wall, 1);
        v.bg.setStrokeStyle(2, PALETTE.tileBorder);
        this.setCellLabel(v, '');
        v.glyph.setVisible(false);
        v.stateFrame.setVisible(false).clear();
        v.mode = 'neutral';
      }
      return;
    }

    const scanned = cell.revealedUntilMs > this.sim.timeMs && !cell.eaten;

    // Decide the target visual mode for this cell this frame.
    let mode: CellMode;
    if (cell.eaten) {
      // M2 + learning loop: every eat (yours OR the AI's) teaches the rule by showing TRUE status.
      mode = cell.kind === 'correct' ? 'eatenCorrect' : 'eatenDecoy';
    } else if (cell.armed) {
      mode = 'armed'; // H1: locked / impassable
    } else if (scanned) {
      mode = cell.kind === 'correct' ? 'revealCorrect' : 'revealDecoy';
    } else {
      mode = 'neutral';
    }

    const modeChanged = mode !== v.mode || scanned !== v.scanned;
    if (modeChanged) {
      this.applyCellMode(cell, v, mode, scanned);
      if (!immediate && mode === 'eatenCorrect') popCorrectClaimTile(this, v, cell.id);
    }

    // SCRAMBLER ANIM: the Scrambler swaps two cells' CONTENT in place, so a non-eaten cell's
    // displayText changes between frames. Detect that here (outside the mode switch, since the mode
    // may not change) and play a quick shuffle/flip so the player notices the board scrambled.
    // (The lie tell relabel ALSO changes displayText, but that path is announced by the glitch tell
    // below; we only shuffle when the text changed for a NON-lying cell, i.e. a true content swap.)
    // (walls already returned above, so cell.kind is correct|decoy here.)
    if (!cell.eaten && cell.displayText !== v.lastDisplayText) {
      const wasLie = v.lying || cell.lying;
      v.lastDisplayText = cell.displayText;
      if (!immediate && !wasLie) this.playScrambleSwap(v, cell.id);
    } else if (cell.eaten) {
      // keep the cache coherent so a later re-fill doesn't spuriously trigger the shuffle.
      v.lastDisplayText = cell.displayText;
    }

    // LIAR TELL: while the snapshot says this cell is lying, show an animated glitch shimmer + a ⚠
    // glyph so the player is WARNED the label may be false. The true `kind` is unchanged (Core),
    // this is purely a visual warning; the label itself stays fully readable.
    const lying = cell.lying && !cell.eaten;
    if (lying !== v.lying) this.setLieTell(v, lying, immediate);

    // Safe-passed cells breathe one scan-blue accent frame + check glyph (cheap alpha pulse on
    // already-built graphics) so the spent path reads as a successful pass, not a wrong munch.
    // Under reduced-motion both hold SOLID — WCAG 2.3.1 / 2.2.2.
    if (mode === 'armed') {
      if (immediate || !motionAllowed()) {
        v.stateFrame.setAlpha(1);
        v.lockIcon?.setAlpha(1);
      } else {
        const t = this.time.now / 1000;
        // S6 armed-tell fix (reassess weakness 4): the old pulse dipped the ring to 0.55 and the padlock
        // to 0.70, so a single mobile capture caught at a trough could read the armed lock as weak/absent
        // (frame-inconsistent). Raise both FLOORS so the gold padlock + amber ring are unmistakable in
        // ANY single frame, and SLOW the breath (5.2→3.4 rad/s) so it reads as a calm "locked" glow, not
        // a fast flicker. Ring 0.82→1.0, padlock 0.9→1.0 — always clearly on, still visibly alive.
        v.stateFrame.setAlpha(0.82 + 0.18 * (0.5 + 0.5 * Math.sin(t * 3.4)));
        v.lockIcon?.setAlpha(0.9 + 0.1 * (0.5 + 0.5 * Math.sin(t * 3.4 + 1.0)));
      }
    }
  }



  /**
   * LIAR TELL on/off. When on: a low-alpha magenta glitch band shimmers (alpha + horizontal jitter)
   * across the tile and a ⚠ glyph appears top-left. The label is untouched (stays readable). When
   * off: stop the tween, hide both overlays, and reset their transforms.
   */
export function setLieTell(this: GameScene, v: CellView, lying: boolean, immediate: boolean): void {
    v.lying = lying;
    if (lying) {
      // Name the CAUSE + scope so it doesn't read like a correctness hint: a Liar monster swapped this
      // tile's word, so the TEXT is a lie right now — it is NOT a "this one is right/wrong" tell.
      if (!immediate) this.showWarningHint('liar', 'LIAR: PURPLE WORDS ARE FAKE — NOT HINTS', PALETTE.ai, 2200);
      v.glitchOverlay.setVisible(true).setFillStyle(PALETTE.ai, 0.36).setAlpha(1).setX(0);
      v.lieGlyph.setVisible(true).setAlpha(1).setColor(hex(PALETTE.gold)).setStroke('#020617', 3);
      v.lieTween?.remove();
      // Reduced-motion: keep the band + ⚠ glyph STATIC (the signal is the persistent shape/colour,
      // not the shimmer) — no infinite blink/jitter. WCAG 2.2.2 (pause/stop moving content).
      if (!immediate && motionAllowed()) {
        // shimmer: pulse the band's alpha + nudge it side-to-side for a "bad signal" glitch read.
        v.lieTween = this.tweens.add({
          targets: v.glitchOverlay,
          alpha: { from: 0.95, to: 0.58 },
          x: { from: -2, to: 2 },
          duration: 160,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
        // a gentle blink on the ⚠ so it reads as an active warning, not a static badge.
        this.tweens.add({
          targets: v.lieGlyph,
          alpha: { from: 1, to: 0.65 },
          duration: 300,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    } else {
      v.lieTween?.remove();
      v.lieTween = undefined;
      this.tweens.killTweensOf(v.glitchOverlay);
      this.tweens.killTweensOf(v.lieGlyph);
      v.glitchOverlay.setVisible(false).setAlpha(1).setX(0);
      v.lieGlyph.setVisible(false).setAlpha(1);
    }
  }



  /**
   * SCRAMBLER ANIM: a quick flip/shuffle on a cell whose content just got swapped — squash the
   * container horizontally to ~0 and back (a "card flip", which visually sells the label changing
   * mid-flip) wrapped in a scan-blue glow ring, plus explicit Scrambler copy above the cell, so
   * the swap reads as a hazard payoff rather than a default idle tile behavior.
   */
export function playScrambleSwap(this: GameScene, v: CellView, cellId: number): void {
    if (!v.container.active) return;
    this.showWarningHint('scrambler', 'SCRAMBLER SWAPPED ANSWERS', PALETTE.scan, 1300);
    const base = v.container.scaleX || 1;
    glowPulse(this, v.container, v.w, v.h, PALETTE.scan, 420);
    this.tweens.add({
      targets: v.container,
      scaleX: { from: base, to: 0.04 },
      duration: 120,
      ease: 'Quad.easeIn',
      yoyo: true,
      onComplete: () => {
        if (v.container.active) v.container.setScale(base);
      },
    });
    // floating shuffle glyph so the scramble reads instantly (and at a glance in a screenshot).
    const c = this.cellCenter(cellId);
    floatText(this, c.x, c.y - v.h * 0.3, 'SCRAMBLER', hex(PALETTE.scan), Math.max(12, v.w * 0.2));
    // a soft "shuffle" blip, throttled by the near-miss cadence field so a burst of swaps doesn't strobe audio.
    if (this.time.now >= this.nextScrambleSfxMs) {
      this.nextScrambleSfxMs = this.time.now + 350;
      audio().playSfx('scan', { volume: 0.5, rate: 0.8 });
      haptic(10);
    }
  }



  /**
   * Crunchier correct-munch on the eaten TILE: a quick scale pop that RETURNS to base scale (never
   * collapses the cell to 0 — the green ✓ reveal must stay visible + legible), a green glow ring,
   * a bright one-shot BLOOM that flashes the cell then fades to its eaten state (so the tile reads as
   * "emptied" rather than snapping), and a crumb burst of correct-color specks. A THREADED gauntlet
   * eat hits harder (brighter bloom + more crumbs). All motion is gated by the juice helpers /
   * motionAllowed(); under reduced-motion the glow + a tiny particle ack still land, the pop+bloom
   * no-op into a clean, instant eaten reveal.
   */
export function crunchCorrectCell(this: GameScene,
    v: CellView,
    at: { x: number; y: number } | null,
    isHuman: boolean,
    threaded = false,
  ): void {
    if (!v.container.active) return;
    const ringColor = threaded ? PALETTE.armed : PALETTE.correct;
    glowPulse(this, v.container, v.w, v.h, ringColor, isHuman ? 460 : 320);
    if (motionAllowed()) {
      const base = v.container.scaleX || 1;
      // Human arrival pop is held long enough to read in 25-30fps capture: the eaten tile visibly
      // "gives" under the chomp, then returns to the base scale so the check reveal stays legible.
      this.tweens.add({
        targets: v.container,
        scale: { from: base, to: base * (isHuman ? 1.5 : 1.2) },
        duration: isHuman ? 112 : 86,
        ease: 'Back.easeOut',
        yoyo: true,
        onComplete: () => {
          if (v.container.active) v.container.setScale(base);
        },
      });
      // EAT BLOOM + EMPTY: a bright additive flash filling the cell that fades out fast, so the tile
      // visibly "empties"/dissolves into its (already-applied) green-✓ eaten state instead of snapping.
      // Lives INSIDE the container at v.container's scene depth (3) — BELOW the muncher (depth 6) — so it
      // never washes over a muncher standing on the eaten cell. SAFETY: it renders over the cell's own
      // label, which is fine ONLY because tickFrame() runs renderSnapshot() (clears the label to '' +
      // shows the centered ✓) BEFORE consumeEvents() fires this bloom; do not reorder those two calls.
      // Intensity kept modest (alpha + 1.09 grow) so a peak-combo CLUSTER of eats stays a crisp pop and
      // doesn't engulf the muncher caught in it; threaded eats bloom a touch brighter.
      const bloom = this.add
        .rectangle(0, 0, v.w, v.h, ringColor, threaded ? 0.48 : 0.36)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(20);
      v.container.add(bloom);
      this.tweens.add({
        targets: bloom,
        alpha: 0,
        scaleX: 1.13,
        scaleY: 1.13,
        duration: threaded ? 360 : 280,
        ease: 'Quad.easeOut',
        onComplete: () => bloom.destroy(),
      });
      const flash = this.add
        .rectangle(0, 0, v.w * 0.82, v.h * 0.82, PALETTE.white, isHuman ? 0.24 : 0.12)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(22);
      v.container.add(flash);
      this.tweens.add({
        targets: flash,
        alpha: 0,
        scaleX: 1.24,
        scaleY: 1.24,
        duration: 128,
        ease: 'Quad.easeOut',
        onComplete: () => flash.destroy(),
      });
    }
    // crumb/spark burst of correct-color specks for the satisfying "pop" (motion-gated inside the
    // helper, which trims the count under reduced-motion). Count kept lean so several near-simultaneous
    // eats at peak combo don't spray a green cluster over the muncher. Threaded eats throw a couple more.
    if (isHuman && at) particleBurst(this, at.x, at.y, ringColor, threaded ? 8 : 5);
  }



  /**
   * Set the bg-rect tint for a cell mode. When tile ART is present the bg rect is a TINT overlay: we
   * honor the colored state signal (eaten/armed) at a low alpha so the art shows through, and drop the
   * neutral/scanned base to fully transparent (the art IS the base). With no art it behaves exactly as
   * before (opaque base, 0.22 colored tints) so the M0/M1 look is byte-for-byte unchanged.
   *   procAlpha = alpha used in the procedural (no-art) path; artAlpha = alpha when art underlays.
   */
export function tintBg(this: GameScene, v: CellView, color: number, procAlpha: number, artAlpha: number): void {
    v.bg.setFillStyle(color, v.tileImg ? artAlpha : procAlpha);
  }



export function drawStateFrame(this: GameScene, v: CellView, color: number, alpha = 0.95): void {
    v.stateFrame.clear();
    v.stateFrame.lineStyle(3, color, alpha);
    v.stateFrame.strokeRect(-v.w / 2 + 1.5, -v.h / 2 + 1.5, v.w - 3, v.h - 3);
    v.stateFrame.setVisible(true).setAlpha(1);
  }



export function setGlyphCorner(this: GameScene, v: CellView): void {
    v.glyph
      .setPosition(v.w / 2 - 3, -v.h / 2 + 2)
      .setOrigin(1, 0)
      .setFontSize(Math.max(11, Math.min(18, Math.floor(v.w * 0.34))));
  }


function drawCornerStateBadge(v: CellView, color: number): void {
    const r = Math.max(9, Math.min(15, Math.floor(Math.min(v.w, v.h) * 0.18)));
    const x = v.w / 2 - r - 4;
    const y = -v.h / 2 + r + 4;
    v.stateFrame.fillStyle(PALETTE.void, 0.78);
    v.stateFrame.fillCircle(x, y, r + 2);
    v.stateFrame.lineStyle(Math.max(2, Math.floor(r * 0.22)), color, 1);
    v.stateFrame.strokeCircle(x, y, r + 1);
    v.stateFrame.setVisible(true).setAlpha(1);
    v.glyph
      .setPosition(x, y - 1)
      .setOrigin(0.5)
      .setFontSize(Math.max(14, Math.floor(r * 1.45)))
      .setStroke('#020617', 3)
      .setShadow(0, 1, '#020617', 2, false, true);
  }



  /** Swap the underlying tile art texture for a state (no-op when tile art isn't wired). */
export function setTileArt(this: GameScene, v: CellView, logicalKey: Parameters<typeof tex>[0]): void {
    if (!v.tileImg || !hasTex(logicalKey)) return;
    v.tileImg.setTexture(tex(logicalKey));
    v.tileImg.setDisplaySize(v.w, v.h);
  }



  /** Re-skin a cell for a (changed) visual mode. Heavier work (glyph text, state frame) lives here. */
export function applyCellMode(this: GameScene, cell: Cell, v: CellView, mode: CellMode, scanned: boolean): void {
    v.mode = mode;
    v.scanned = scanned;
    v.fittedText = '';
    // reset overlays; each branch re-enables what it needs. Reset glyph/frame ALPHA too: the armed
    // danger-throb leaves them mid-pulse, so a transition out of armed must clear that residual alpha
    // (otherwise a freshly-eaten/scanned glyph could inherit a dimmed value).
    v.glyph
      .setVisible(false)
      .setAlpha(1)
      .setPosition(v.w / 2 - 3, -v.h / 2 + 2)
      .setOrigin(1, 0)
      .setFontSize(Math.max(11, Math.min(18, Math.floor(v.w * 0.34))))
      .setStroke('#020617', 0)
      .setShadow(0, 0, '#000000', 0, false, false);
    v.stateFrame.setVisible(false).setAlpha(1).clear();
    v.labelPlate.setVisible(false).clear();
    v.lockIcon?.setVisible(false).setAlpha(1).setPosition(0, 0).clear();
    v.label.setY(0).setAlpha(1).setStroke('#020617', 2);

    switch (mode) {
      case 'eatenCorrect': {
        // Redline: correct fill + correct border + corner check. Label stays (teaches true status).
        // M3: beefier eaten fill (0.16/0.2 -> 0.22/0.26) so a cleared correct reads as a solidly-filled
        // "eaten" tile, not a faint wash — white label + stroke still clears contrast at this alpha.
        this.setTileArt(v, 'cellEatenCorrect');
        this.tintBg(v, PALETTE.correct, 0.22, 0.26);
        v.bg.setStrokeStyle(1.5, PALETTE.correct, 1);
        this.setCellLabel(v, cell.displayText);
        v.label.setColor(hex(PALETTE.white)).setAlpha(1);
        drawCornerStateBadge(v, PALETTE.correct);
        v.glyph.setText('✓').setColor(hex(PALETTE.white)).setVisible(true);
        break;
      }
      case 'eatenDecoy': {
        // Redline-symmetric: hazard fill + hazard border + corner ✗. M3: beefier eaten fill (0.16/0.2 ->
        // 0.22/0.26) to match the correct-eaten weight so both cleared states read solidly filled.
        this.setTileArt(v, 'cellEatenDecoy');
        this.tintBg(v, PALETTE.hazard, 0.22, 0.26);
        v.bg.setStrokeStyle(1.5, PALETTE.hazard, 1);
        this.setCellLabel(v, cell.displayText);
        v.label.setColor(hex(PALETTE.white)).setAlpha(1);
        drawCornerStateBadge(v, PALETTE.hazard);
        v.glyph.setText('✗').setColor(hex(PALETTE.white)).setVisible(true);
        break;
      }
      case 'armed': {
        // Redline: lockFill(#232838) + amber ring + centred padlock. A routed-through DECOY you skipped is
        // now LOCKED/impassable — the answer stays readable above the padlock while the cost badge owns
        // the top band, so a rival standing here cannot erase the risk/reward read.
        this.setTileArt(v, 'cellArmed');
        this.tintBg(v, PALETTE.lockFill, 1, 0.5);
        v.bg.setStrokeStyle(1.5, PALETTE.armed, 0.9);
        this.drawStateFrame(v, PALETTE.armed, 0.95);
        this.setCellLabel(v, cell.displayText);
        styleArmedAnswerLabel(v);
        if (v.lockIcon) {
          v.lockIcon.clear();
          // S6: a soft dark backing disc UNDER the padlock guarantees amber-on-dark contrast on any tile
          // art/frame, so the lock never washes out on a single mobile frame without crowding the label.
          const lockR = Math.min(v.w, v.h) * 0.38;
          v.lockIcon.fillStyle(PALETTE.void, 0.55);
          v.lockIcon.fillCircle(0, 0, lockR * 0.62);
          drawIcon(v.lockIcon, 'lock', lockR, PALETTE.armed);
          v.lockIcon.setVisible(true).setAlpha(1).setY(v.h * 0.17);
        }
        v.glyph
          .setText('2 CHOMPS')
          .setFontSize(Math.max(10, Math.min(12, Math.floor(v.w * 0.16))))
          .setColor(hex(PALETTE.armed))
          .setStroke('#020617', 3)
          .setOrigin(0.5)
          .setAlign('center')
          .setPosition(0, -v.h * 0.31)
          .setVisible(true);
        break;
      }
      case 'revealCorrect':
      case 'revealDecoy': {
        // Redline: scan border + corner glyph (the glyph colour still carries the true status).
        const c = mode === 'revealCorrect' ? PALETTE.correct : PALETTE.hazard;
        this.setTileArt(v, 'cellScanned');
        this.tintBg(v, PALETTE.tile, 1, 0);
        v.bg.setStrokeStyle(1.5, PALETTE.scan, 0.6);
        this.drawStateFrame(v, PALETTE.scan, 0.95);
        // M6: a bold hazard X-STRIKE across a SCANNED DECOY so the "wrong" verdict reads at a glance
        // WITHOUT the transient scan toast (mock-2). Drawn INTO the state-frame graphics (which sits below
        // the label in the container), so the two thick strokes cross BEHIND the still-readable word — it
        // reads as "crossed out". Colourblind-safe: the X shape + the corner ✗ both carry it, not colour.
        // Cleared with the frame on the next mode change (applyCellMode resets stateFrame). Decoy only —
        // a scanned CORRECT keeps its clean scan border + corner ✓.
        if (mode === 'revealDecoy') {
          // Keep the wrong-answer stamp inset so the animated scan pulse owns the tile edge instead of
          // tangling with it in motion-review frames. The corner/colour still carry the verdict, but the
          // ring remains clean at phone size.
          const m = Math.min(v.w, v.h) * 0.23;
          v.stateFrame.lineStyle(Math.max(2, v.w * 0.04), PALETTE.hazard, 0.68);
          v.stateFrame.lineBetween(-m, -m, m, m);
          v.stateFrame.lineBetween(m, -m, -m, m);
        }
        if (mode === 'revealCorrect') {
          drawCornerStateBadge(v, c);
          v.glyph.setText('✓').setColor(hex(PALETTE.white)).setVisible(true);
        } else {
          this.setGlyphCorner(v);
          v.glyph.setText('✗').setColor(hex(c)).setVisible(false);
        }
        this.setCellLabel(v, cell.displayText);
        v.label.setColor(hex(PALETTE.white)).setAlpha(1);
        if (mode === 'revealDecoy') v.label.setY(-v.h * 0.28).setStroke('#020617', 3);
        break;
      }
      default: {
        // neutral — tile fill + theme-blended hairline (decoration; see themeCellStroke) + label.
        this.setTileArt(v, 'cellNeutral');
        this.tintBg(v, PALETTE.tile, 1, 0);
        v.bg.setStrokeStyle(1, this.themeCellStroke);
        this.setGlyphCorner(v);
        this.setCellLabel(v, cell.displayText);
        v.label.setColor(hex(PALETTE.white)).setAlpha(1);
        break;
      }
    }
  }



  /**
   * Pop an art VFX sprite at (x,y) that scales up + fades out, then self-destroys. Additive juice ON
   * TOP of the procedural particle burst — no-op when the VFX art isn't wired. Never occludes text:
   * it's transient and centered on the action, removed within ~durMs.
   */
export function playVfx(
    this: GameScene,
    logicalKey: Parameters<typeof tex>[0],
    x: number,
    y: number,
    size: number,
    durMs = 380,
    clipToBoard = false,
  ): void {
    if (!hasTex(logicalKey)) return;
    const img = this.add.image(x, y, tex(logicalKey)).setDepth(9200);
    // S3 item 8: the SCAN ripple is drawn ~2.4× a cell wide and centred on the scanned cell, so an
    // edge-column scan let it wash off-screen. Clip it to the board rect so it never bleeds past the grid.
    if (clipToBoard) img.setMask(this.ensureBoardClipMask());
    this.fitImageTo(img, size);
    const base = img.scale;
    const scanRipple = logicalKey === 'vfxScanRipple';
    img.setScale(base * 0.5).setAlpha(scanRipple ? 0.38 : 0.95);
    this.tweens.add({
      targets: img,
      scale: base * (scanRipple ? 0.95 : 1.15),
      alpha: 0,
      duration: durMs,
      ease: 'Quad.easeOut',
      onComplete: () => img.destroy(),
    });
  }



  /**
   * Lazily build (and cache) a geometry mask covering the board rect, used to clip board-local VFX
   * (the SCAN ripple) so they can never bleed past the grid edges. The mask Graphics is invisible and
   * lives at the board's screen rect; it's torn down with the scene. Rebuilt if the board rect changed.
   */
export function ensureBoardClipMask(this: GameScene): Phaser.Display.Masks.GeometryMask {
    const boardH = this.cellH * this.rows;
    if (!this.boardClipGfx || !this.boardClipGfx.active) {
      const g = this.add.graphics().setVisible(false);
      g.fillStyle(0xffffff, 1);
      g.fillRect(this.gridX, this.gridY, this.boardW, boardH);
      this.boardClipGfx = g;
      this.boardClipMask = g.createGeometryMask();
    }
    return this.boardClipMask!;
  }



  /**
   * HAZARD TELEGRAPH (renderer-only). A brief warning pre-flash + a couple of particles on a target
   * cell so a hazard's arrival/imminent step is signalled ≥1 beat before it can hurt you — a death
   * should feel earned, not cheap. Derived purely from the rendered hazard position / spawn cell (no
   * Core change, no invented events). Motion-gated: under reduced-motion it draws a single calm static
   * ring (no strobe) instead of a pulse. Self-destructing; one transient object per call (no per-frame
   * allocation in the steady state — the per-step caller is throttled via hazardTelegraphAt).
   */
export function hazardTelegraph(this: GameScene, cellId: number, urgent: boolean): void {
    const v = this.cellViews[cellId];
    if (!v || !v.container.active) return;
    const c = this.cellCenter(cellId);
    const color = urgent ? PALETTE.hazard : PALETTE.armed; // red for a spawn/imminent step, amber otherwise
    this.showWarningHint(
      urgent ? 'hazard-urgent' : 'hazard-near',
      urgent ? 'DANGER: HAZARD INCOMING' : 'WARNING: HAZARD NEARBY',
      color,
      urgent ? 900 : 1500,
    );
    const reduced = !motionAllowed();
    // Draw the ring CENTRED at the graphics origin (0,0) and position the object at the cell centre so a
    // scale pulse grows around the cell (not the world origin).
    const g = this.add.graphics().setDepth(6).setPosition(c.x, c.y);
    g.lineStyle(reduced ? 2 : 3, color, reduced ? 0.5 : 0.9);
    g.strokeRect(-v.w / 2, -v.h / 2, v.w, v.h);
    const r = Math.max(8, Math.min(13, Math.min(v.w, v.h) * 0.16));
    const badge = this.add
      .graphics()
      .setDepth(6.2)
      .setPosition(c.x + v.w / 2 - r * 0.75, c.y - v.h / 2 + r * 0.75);
    badge.fillStyle(PALETTE.void, 0.72);
    badge.fillCircle(0, 0, r * 0.78);
    badge.lineStyle(Math.max(2, r * 0.16), color, 0.95);
    badge.strokeCircle(0, 0, r * 0.78);
    badge.fillStyle(color, 0.96);
    badge.fillTriangle(0, -r * 0.58, -r * 0.5, r * 0.42, r * 0.5, r * 0.42);
    badge.fillStyle(PALETTE.void, 1);
    badge.fillRoundedRect(-r * 0.07, -r * 0.28, r * 0.14, r * 0.42, r * 0.05);
    badge.fillCircle(0, r * 0.26, r * 0.08);
    if (reduced) {
      // calm, no flash: hold a faint ring briefly then fade.
      this.tweens.add({
        targets: [g, badge],
        alpha: 0,
        delay: 300,
        duration: 340,
        onComplete: () => {
          g.destroy();
          badge.destroy();
        },
      });
      return;
    }
    g.setAlpha(0);
    badge.setAlpha(0);
    this.tweens.add({
      targets: [g, badge],
      alpha: { from: 0, to: 0.95 },
      scaleX: { from: 1.12, to: 1 },
      scaleY: { from: 1.12, to: 1 },
      duration: 150,
      yoyo: true,
      hold: 40,
      ease: 'Quad.easeOut',
      onComplete: () => {
        g.destroy();
        badge.destroy();
      },
    });
  }



  /** Glow-pulse the just-scanned cell + its orthogonal neighbors (mirrors applyScan's reveal set). */
export function pulseScanReveal(this: GameScene, snap: Snapshot, centerId: number): void {
    const ids = [centerId];
    const col = centerId % this.cols;
    const row = Math.floor(centerId / this.cols);
    if (col > 0) ids.push(centerId - 1);
    if (col < this.cols - 1) ids.push(centerId + 1);
    if (row > 0) ids.push(centerId - this.cols);
    if (row < this.rows - 1) ids.push(centerId + this.cols);
    for (const id of ids) {
      const cell = snap.cells[id];
      const v = this.cellViews[id];
      if (!cell || !v || cell.kind === 'wall' || !v.container.active) continue;
      const color = cell.eaten
        ? cell.kind === 'correct'
          ? PALETTE.correct
          : PALETTE.hazard
        : PALETTE.scan;
      glowPulse(this, v.container, v.w, v.h, color, 520);
    }
  }
